import { CaretDownFilled, CaretDownOutlined } from '@ant-design/icons'
import cls from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { TextContent } from '@/components'
import { ParentCommentsFragment, PostType, RequestedPostFragment, usePostUpdateMutation } from '@/generated/graphql'
import { ContentModeContext } from '@/modules/user/user-timeline/user-timeline-context'
import { routes } from '@/routes'

import { PostCardFiles } from '../post-card-files.component'
import { PostCardHeader } from '../post-card-header.component'
import { PostCardLinks } from '../post-card-links.component'
import s from '../post-card.module.less'
import { SpecialTemplate } from './post-contents-special.component'

interface PostCardContentProps {
  post: RequestedPostFragment & ParentCommentsFragment
  isGroup: boolean
  isUser: boolean
  isSection: boolean
  isTimeline?: boolean
  isSinglePost?: boolean
  hasDescendants?: boolean
  isRecommended?: boolean
  disablePics?: boolean
  disableBackground?: boolean
}

export const PostCardContent: React.FC<PostCardContentProps> = ({
  post,
  isGroup,
  isUser,
  isSection,
  isTimeline = false,
  isSinglePost = false,
  isRecommended = false,
  hasDescendants = false,
  disablePics = false,
  disableBackground = false,
}) => {
  const [t] = useTranslation()
  const specialPostTypes = [PostType.Introduction, PostType.Event, PostType.Forum, PostType.Thanks, PostType.Honor]
  const contentRef = React.useRef<HTMLDivElement>(null)
  const [shouldExpand, setShouldExpand] = React.useState(false)
  const [isExpanded, setIsExpanded] = React.useState(
    (!post.isRead && !post.isViewerCreated) || specialPostTypes.includes(post.postType) || isSinglePost
  )
  const history = useHistory()

  const [updatePost] = usePostUpdateMutation()

  const mode = React.useContext(ContentModeContext)

  React.useLayoutEffect(() => {
    if (contentRef.current) {
      setShouldExpand(contentRef.current.offsetHeight > 160)
    }
    if (hasDescendants) {
      setIsExpanded(true)
    }
  }, [contentRef.current])

  const onContentChange = React.useCallback(
    (content: string) => {
      if (post.isViewerCreated) {
        updatePost({
          variables: {
            postId: post.postId,
            post: { content, isMarkdown: post.isMarkdown },
          },
        })
      }
    },
    [post]
  )

  const originalPost =
    post.originalPostId &&
    (post.originalPost ? (
      <div
        className={s.originalPostContainer}
        style={{ margin: isTimeline ? 16 : 0, marginTop: 8 }}
        onClick={() => !isTimeline && history.push(routes.posts.page(post.originalPostId!))}
      >
        {!isTimeline && (
          <PostCardHeader
            hideMenu
            post={post.originalPost as any}
            isGroup={isGroup}
            isUser={isUser}
            isSection={isSection}
            showGroupInfo
            isTop={false}
            hideAvatar={true}
            isRecommended={isRecommended}
          />
        )}
        {isTimeline && (
          <div style={{ marginBottom: 8 }}>
            <CaretDownOutlined /> {t('post.sharedPost')}
          </div>
        )}
        <PostCardContent
          hasDescendants={true}
          post={post.originalPost as any}
          isGroup={isGroup}
          isUser={isUser}
          isSection={isSection}
        />
      </div>
    ) : (
      <div className={cls(s.originalPostContainer, s.archivedPost)}>{t('post.archived')}</div>
    ))

  const showExpandButton = shouldExpand && !isExpanded && !hasDescendants
  const expandButton = showExpandButton && (
    <>
      <div className={s.expandButton} onClick={() => setIsExpanded(true)}>
        <CaretDownFilled />
        {t('post.expand')}
      </div>
    </>
  )

  const blurDiv = showExpandButton && <div className={s.blurDiv} />

  const content = (
    <div
      className={cls(s.content, {
        'is-not-expanded': showExpandButton,
        'is-expanded': !mode.isSimplified && isExpanded,
        'is-simplified': mode.isSimplified,
      })}
    >
      <div ref={contentRef}>
        {specialPostTypes.includes(post.postType!) ? (
          <SpecialTemplate post={post} isRecommended={isRecommended} disableBackground={disableBackground} />
        ) : (
          <TextContent
            content={post.content.slice(0, mode.wordLength)}
            mentions={post.mentions}
            isMarkdown={post.isMarkdown}
            onChange={onContentChange}
          />
        )}
        {originalPost}
      </div>
      {blurDiv}
    </div>
  )

  return (
    <div className={cls(s.contentContainer)}>
      {(!mode.isSimplified || post.content || post.originalPost) && content}
      {!isTimeline && expandButton}
      {!disablePics && (!mode.isSimplified || !post.content) && <PostCardFiles files={post.files} />}

      {!mode.isSimplified && <PostCardLinks links={post.links} />}
    </div>
  )
}
